/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Vers.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, JSX } from 'preact';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { usePress } from '#hooks/UNSAFE_usePress';
import { ButtonLabelLayout } from '#UNSAFE_ButtonLabelLayout';
import { styles } from '#UNSAFE_BaseButton/themes/BaseButtonStyles.css';
import { classNames } from '#/utils/UNSAFE_classNames';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';

type IntrinsicProps = Pick<
  UnpackSignals<JSX.HTMLAttributes<HTMLElement>>,
  | 'autofocus'
  | 'onBlur'
  | 'onFocus'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onTouchEnd'
  | 'onTouchStart'
>;

export type Props = IntrinsicProps & {
  /**
   * Specifies that the button separator should be rendered disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies that the input is intended for use in a switch button
   */
  isSwitch?: boolean;

  /**
   * Specifies if input is checked or not, controlled pattern
   */
  isChecked?: boolean;

  /**
   * onAction is invoked when then input is pressed
   */
  onAction?: (event: Event) => void;

  /**
   * inputType specifies what type of input is needed
   */
  inputType?: 'radio' | 'checkbox';

  /**
   * inputName is the name of the radio or checkbox
   */
  inputName?: string;

  /**
   * value is the value of the radio or checkbox
   */
  value?: string;

  /**
   * inputId is the id used by the label
   */
  inputId?: string;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * The endIcon is the button's end icon.
   */
  endIcon?: ComponentChildren;

  /**
   * Display indicates whether only the label or all elements should be rendered by the togglebutton.
   */
  display?: 'label' | 'all' | 'icons';

  /**
   * The size indicates how large the togglebutton is rendered.
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * label - togglebutton label
   */
  label?: string;
};

export const ToggleButtonInput = ({
  isDisabled,
  isChecked,
  isSwitch,
  inputName,
  inputType,
  value,
  inputId,
  startIcon,
  endIcon,
  display,
  onAction,
  size,
  label,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  onTouchEnd,
  onTouchStart
}: Props) => {
  const { pressProps } = usePress((e) => {
    onAction?.(e);
  });
  const { tabbableModeProps } = useTabbableMode(isDisabled);

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        event.preventDefault();
        break;
      }
    }
  };
  const keyProps = { onKeyDown };

  const mergedProps = mergeProps(pressProps, tabbableModeProps, keyProps);
  const inputElement = (
    <HiddenAccessible>
      <input
        {...(isSwitch ? { role: 'switch' } : {})}
        type={inputType}
        checked={isChecked}
        name={inputName}
        value={value}
        disabled={isDisabled}
        {...mergedProps}
        id={inputId}></input>
    </HiddenAccessible>
  );

  const isLabel = (!startIcon && !endIcon) || display === 'label';

  const isIcon = display === 'icons';

  const wrapperClasses = classNames([
    styles.inputLabel,
    !isIcon ? styles.labelSizes[size ?? 'md'] : undefined,
    !isLabel && styles.min
  ]);

  // For input types, must use labels and modify approach for button
  return isLabel ? (
    <label
      for={inputId}
      class={wrapperClasses}
      onBlur={(event) => {
        onBlur?.(event);
      }}
      onFocus={(event) => {
        onFocus?.(event);
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchEnd={onTouchEnd}
      onTouchStart={onTouchStart}>
      {label}
      {inputElement}
    </label>
  ) : (
    <label
      for={inputId}
      class={wrapperClasses}
      onBlur={(event) => {
        onBlur?.(event);
      }}
      onFocus={(event) => {
        onFocus?.(event);
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchEnd={onTouchEnd}
      onTouchStart={onTouchStart}>
      <ButtonLabelLayout display={display} startIcon={startIcon} endIcon={endIcon} size={size}>
        {label}
      </ButtonLabelLayout>
      {inputElement}
      {display === 'icons' ? <HiddenAccessible>{label}</HiddenAccessible> : undefined}
    </label>
  );
};

ToggleButtonInput.displayName = 'ToggleButtonInput';
